import React, { useEffect, useState, useRef } from 'react';
import BaseLayout from './BaseLayout';
import "../App.css";
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { API_BASE } from '../api';
import { useAuth } from '../context/AuthContext';
import { useView } from '../context/ViewContext';
import FlameLoader from './FlameLoader';
import ParticlesBg from './ParticlesBg';
import ProfileStats from './ProfileStats';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

const BADGE_META = {
    admin:         { label: 'Admin',            cls: 'badge-admin'        },
    founder:       { label: 'Founder',          cls: 'badge-info'         },
    '1st':         { label: 'Leaderboard: 1st', cls: 'badge-gold'         },
    '2nd':         { label: 'Leaderboard: 2nd', cls: 'badge-silver'       },
    '3rd':         { label: 'Leaderboard: 3rd', cls: 'badge-bronze'       },
    fire_lord:     { label: 'Fire Lord',        cls: 'badge-fire-lord'    },
    goat:          { label: 'GOAT',             cls: 'badge-goat'         },
    legend:        { label: 'Legend',           cls: 'badge-legend'       },
    elite:         { label: 'Elite',            cls: 'badge-elite'        },
    flame_prince:  { label: 'Flame Prince',     cls: 'badge-flame-prince' },
    popular:       { label: 'Popular',          cls: 'badge-popular'      },
    perfectionist: { label: 'Perfectionist',    cls: 'badge-perfectionist'},
    poop:          { label: '💩',               cls: 'badge-poop'         },
    all_rounder:   { label: 'All-Rounder',      cls: 'badge-all-rounder'  },
    traitor:       { label: 'Traitor',          cls: 'badge-traitor'      },
    friendly:      { label: 'Friendly',         cls: 'badge-friendly'     },
    nostalgic:     { label: 'Nostalgic',        cls: 'badge-nostalgic'    },
};

const ProfilePage = () => {
    const { authData } = useAuth();
    const { viewID } = useView();
    const [loading, setLoading] = useState(true);
    const [isFriend, setIsFriend] = useState(false);
    const [hasSentRequest, setHasSentRequest] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [countedWins, setCountedWins] = useState(0);
    const [countedLosses, setCountedLosses] = useState(0);
    const [statsVisible, setStatsVisible] = useState(false);
    const statsRef = useRef(null);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [messageText, setMessageText] = useState('');
    const [messageSending, setMessageSending] = useState(false);
    const [messageSent, setMessageSent] = useState(false);
    const [messageError, setMessageError] = useState('');

    const navigate = useNavigate();
    const isOwnProfile = !viewID || viewID === authData.user?.id;

    const handleTeamClick = async (teamName) => {
        try {
            const res = await axios.get(`${API_BASE}/sports/search-teams`, { params: { q: teamName.toLowerCase() } });
            const team = res.data.teams?.[0];
            if (team?.idTeam) navigate(`/team/${team.idTeam}`);
        } catch {}
    };

    useEffect(() => {
        document.body.classList.add("account-page-bg");

        const fetchData = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const userId = authData.user?.id;
                const targetId = isOwnProfile ? userId : viewID;

                const userResp = await axios.get(`${API_BASE}/data/get-user/${targetId}`);
                const u = userResp.data;

                let customization = u.customization || null;

                if (isOwnProfile) {
                    const customResp = await axios.get(`${API_BASE}/user/profile-customization`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    customization = customResp.data.customization;
                }

                setUserInfo({
                    id: u.id,
                    firstName: u.firstName,
                    lastName: u.lastName,
                    username: u.username,
                    profilePicture: u.profile_picture,
                    score: u.score ?? 0,
                    losses: u.losses ?? 0,
                    favoriteNFLTeam: u.favorite_nfl_team,
                    favoriteNBATeam: u.favorite_nba_team,
                    rank: u.rank,
                    badges: u.badges || [],
                    customization,
                });

                if (!isOwnProfile) {
                    const [friendResp, reqResp] = await Promise.all([
                        axios.get(`${API_BASE}/user/check-friendship/${userId}/${u.id}`),
                        axios.get(`${API_BASE}/user/check-friend-request/${userId}/${u.id}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        })
                    ]);
                    setIsFriend(friendResp.data.isFriend);
                    setHasSentRequest(reqResp.data.hasSentRequest);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (authData.user?.id) fetchData();
        return () => document.body.classList.remove("account-page-bg");
    }, [viewID, authData.user?.id, isOwnProfile]);

    // Intersection observer for count-up animation
    useEffect(() => {
        if (!statsRef.current) return;
        const obs = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setStatsVisible(true); obs.disconnect(); }
        }, { threshold: 0.3 });
        obs.observe(statsRef.current);
        return () => obs.disconnect();
    }, [userInfo]);

    // Count-up when stats become visible
    useEffect(() => {
        if (!statsVisible || !userInfo) return;
        const target_w = userInfo.score ?? 0;
        const target_l = userInfo.losses ?? 0;
        const steps = 40;
        const interval = 800 / steps;
        let step = 0;
        const timer = setInterval(() => {
            step++;
            const ease = 1 - Math.pow(1 - step / steps, 3);
            setCountedWins(Math.round(target_w * ease));
            setCountedLosses(Math.round(target_l * ease));
            if (step >= steps) clearInterval(timer);
        }, interval);
        return () => clearInterval(timer);
    }, [statsVisible, userInfo]);

    const sendFriendRequest = async () => {
        try {
            await axios.post(`${API_BASE}/user/send-friend-request`, {
                sender_id: authData.user?.id,
                recipient_id: viewID
            });
            setHasSentRequest(true);
        } catch (error) {
            console.error('Sending friend request failed:', error);
        }
    };

    const handleSendMessage = async () => {
        if (!messageText.trim()) return;
        setMessageSending(true);
        setMessageError('');
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE}/user/send-message`,
                { recipient_id: userInfo.id, content: messageText.trim() },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setMessageSent(true);
            setMessageText('');
        } catch (err) {
            setMessageError(err.response?.data?.error || 'Failed to send. Please try again.');
        } finally {
            setMessageSending(false);
        }
    };

    const closeMessageModal = () => {
        setShowMessageModal(false);
        setMessageText('');
        setMessageSent(false);
        setMessageError('');
    };

    const wins    = userInfo?.score  ?? 0;
    const losses  = userInfo?.losses ?? 0;
    const total   = wins + losses;
    const winRate = total > 0 ? Math.round((wins / total) * 100) : null;

    // Derive cosmetics from server-validated customization
    const c              = userInfo?.customization;
    const particles      = c?.particles     || 'none';
    const bannerSpecial  = c?.banner_special || 'none';
    const bannerColor1   = c?.banner_color1  || '';
    const bannerColor2   = c?.banner_color2  || '';
    const cardSpecial    = c?.card_special   || 'none';
    const cardAnimMode   = c?.card_anim      || 'static';
    const borderSpecial  = c?.border_special || 'none';
    const borderColor1   = c?.border_color1  || '';
    const borderColor2   = c?.border_color2  || '';
    const borderAnimMode = c?.border_anim    || 'static';
    const nameSpecial    = c?.name_special   || 'default';
    const nameColor1     = c?.name_color1    || '#e05c00';
    const nameColor2     = c?.name_color2    || '#ffd200';
    const nameAnimMode   = c?.name_anim      || 'static';
    const tagline        = c?.tagline        || '';

    const bannerCls = bannerSpecial !== 'none'
        ? ` ap-banner--${bannerSpecial}`
        : bannerColor1 ? ' ap-banner--custom' : '';
    const cardCls = cardSpecial !== 'none'
        ? ` ap-header-card--${cardSpecial}`
        : cardAnimMode !== 'static' ? ` ap-header-card--custom-${cardAnimMode}` : '';
    const bannerStyle = bannerSpecial === 'none'
        ? { position: 'relative', overflow: 'hidden', '--banner-c1': bannerColor1, '--banner-c2': bannerColor2 }
        : { position: 'relative', overflow: 'hidden' };
    const ringCls = borderSpecial !== 'none'
        ? ` ap-avatar-ring--${borderSpecial}`
        : borderColor1 ? ` ap-avatar-ring--custom-${borderAnimMode}` : '';
    const ringStyle = borderSpecial === 'none'
        ? { '--ring-c1': borderColor1, '--ring-c2': borderColor2 }
        : {};
    const nameCls = nameSpecial !== 'default'
        ? ` ap-hero-name--${nameSpecial}`
        : nameAnimMode !== 'static' ? ` ap-hero-name--custom-${nameAnimMode}` : '';
    const nameStyle = nameSpecial === 'default'
        ? { '--name-c1': nameColor1, '--name-c2': nameColor2 }
        : {};

    return (
        <BaseLayout>
            <div className="ap-page">
                {loading ? (
                    <FlameLoader text="Loading profile..." />
                ) : !userInfo ? (
                    <p style={{ textAlign: 'center', color: 'var(--text-dim)', paddingTop: '3rem' }}>User not found.</p>
                ) : (
                    <>
                        {/* ── Space atmosphere ── */}
                        {particles === 'stars' && (
                            <>
                                <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 90% 80% at 50% 40%, #020b1c 0%, #010510 50%, #000000 100%)' }} />
                                <div className="space-nebula-purple" style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 55% 45% at 25% 35%, rgba(90,0,180,0.28) 0%, rgba(50,0,120,0.1) 45%, transparent 68%)' }} />
                                <div className="space-nebula-blue" style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 50% 50% at 75% 65%, rgba(0,120,220,0.22) 0%, rgba(0,60,160,0.08) 45%, transparent 68%)' }} />
                                <div style={{ position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none' }}><ParticlesBg preset="stars" /></div>
                                <div className="space-shooting-star space-shooting-star-1" style={{ zIndex: 9999 }} />
                                <div className="space-shooting-star space-shooting-star-2" style={{ zIndex: 9999 }} />
                                <div className="space-shooting-star space-shooting-star-3" style={{ zIndex: 9999 }} />
                                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '25vh', zIndex: 2, pointerEvents: 'none', background: 'linear-gradient(to bottom, rgba(0,0,5,0.85) 0%, transparent 100%)' }} />
                                <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: '20vh', zIndex: 2, pointerEvents: 'none', background: 'linear-gradient(to top, rgba(0,0,5,0.85) 0%, transparent 100%)' }} />
                                <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: '16vw', zIndex: 2, pointerEvents: 'none', background: 'linear-gradient(to right, rgba(0,0,8,0.78) 0%, transparent 100%)' }} />
                                <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '16vw', zIndex: 2, pointerEvents: 'none', background: 'linear-gradient(to left, rgba(0,0,8,0.78) 0%, transparent 100%)' }} />
                            </>
                        )}
                        {/* ── Fire atmosphere ── */}
                        {particles === 'fire' && (
                            <>
                                <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 80% 40% at 50% 115%, rgba(160,20,0,0.3) 0%, transparent 70%)' }} />
                                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '30vh', zIndex: 0, pointerEvents: 'none', background: 'linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, transparent 100%)' }} />
                                <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: '18vw', zIndex: 9999, pointerEvents: 'none', background: 'linear-gradient(to right, rgba(0,0,0,0.82) 0%, transparent 100%)' }} />
                                <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '18vw', zIndex: 9999, pointerEvents: 'none', background: 'linear-gradient(to left, rgba(0,0,0,0.82) 0%, transparent 100%)' }} />
                                <div className="fire-bottom-glow" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: '28vh', zIndex: 9999, pointerEvents: 'none', background: ['radial-gradient(ellipse 75% 90% at 50% 100%, rgba(255,100,0,0.42) 0%, rgba(200,40,0,0.18) 45%, transparent 72%)', 'linear-gradient(to top, rgba(3,0,0,0.97) 0%, rgba(3,0,0,0.65) 55%, transparent 100%)'].join(', '), animation: 'fire-bottom-pulse 3s ease-in-out infinite' }} />
                            </>
                        )}

                        <div style={{ position: 'relative', zIndex: 10 }}>
                            {/* ── Header card ── */}
                            <div className={`ap-header-card${bannerCls}${cardCls}`} style={bannerStyle}>
                                {bannerSpecial === 'heat' && [
                                    { left: '5%',  w: '14%', skew: -12, delay: '0s',    dur: '3.4s'  },
                                    { left: '16%', w: '10%', skew:   6, delay: '0.65s', dur: '4.0s'  },
                                    { left: '27%', w: '17%', skew:  -7, delay: '1.25s', dur: '3.1s'  },
                                    { left: '40%', w: '11%', skew:  10, delay: '0.2s',  dur: '3.8s'  },
                                    { left: '52%', w: '19%', skew:  -5, delay: '1.75s', dur: '2.85s' },
                                    { left: '64%', w: '12%', skew:   8, delay: '0.9s',  dur: '4.15s' },
                                    { left: '76%', w: '15%', skew:  -9, delay: '1.5s',  dur: '3.55s' },
                                    { left: '87%', w:  '9%', skew:   4, delay: '0.4s',  dur: '3.25s' },
                                ].map((f, i) => (
                                    <div key={i} className="ap-heat-flame" style={{ left: f.left, width: f.w, transform: `translateX(-50%) skewX(${f.skew}deg)`, animationDelay: f.delay, animationDuration: f.dur }} />
                                ))}
                                {particles !== 'none' && particles !== 'stars' && (
                                    <div style={{ position: 'absolute', inset: 0, zIndex: 9, pointerEvents: 'none', borderRadius: 'inherit', overflow: 'hidden' }}>
                                        <ParticlesBg preset={particles} />
                                    </div>
                                )}
                                <div className="ap-header-card-inner">
                                    {isOwnProfile ? (
                                        <div className="ap-friend-btn-wrap">
                                            <Link to="/profile/edit" className="ap-friend-btn">
                                                <i className="fa fa-pencil" aria-hidden="true" /> Edit Profile
                                            </Link>
                                        </div>
                                    ) : (
                                        <div className="ap-friend-btn-wrap">
                                            <button
                                                className="ap-msg-btn"
                                                onClick={() => setShowMessageModal(true)}
                                                title={`Send a message to ${userInfo.firstName}`}
                                            >
                                                <i className="fa fa-envelope" aria-hidden="true" />
                                            </button>
                                            <button
                                                className={isFriend ? 'ap-friend-btn ap-friend-btn--muted' : 'ap-friend-btn'}
                                                onClick={sendFriendRequest}
                                                disabled={isFriend || hasSentRequest}
                                            >
                                                {isFriend ? 'Friends' : hasSentRequest ? 'Request Sent' : 'Add Friend'}
                                            </button>
                                        </div>
                                    )}

                                    <div className={`ap-avatar-ring${ringCls}`} style={ringStyle}>
                                        <a href={userInfo.profilePicture || '/default.jpg'} target="_blank" rel="noopener noreferrer" title="View full image">
                                            <img className="ap-avatar" src={userInfo.profilePicture || '/default.jpg'} alt={`${userInfo.firstName}'s profile`} />
                                        </a>
                                    </div>

                                    <div className="ap-hero-info">
                                        <p className={`ap-hero-name${nameCls}`} style={nameStyle}>{userInfo.firstName} {userInfo.lastName}</p>
                                        {tagline && <p className="ap-tagline">{tagline}</p>}
                                        {userInfo.username && <p className="ap-hero-username">@{userInfo.username}</p>}
                                        {userInfo.badges.length > 0 && (
                                            <div className="ap-badges">
                                                {userInfo.badges.map(key => {
                                                    const meta = BADGE_META[key];
                                                    if (!meta) return null;
                                                    return <span key={key} className={`badge ${meta.cls}`}>{meta.label}</span>;
                                                })}
                                            </div>
                                        )}
                                        <div className="ap-stats-row" ref={statsRef}>
                                            <div className="ap-stat-tile">
                                                <div className="ap-stat-value green">{statsVisible ? countedWins : 0}</div>
                                                <div className="ap-stat-label">Wins</div>
                                            </div>
                                            <div className="ap-stat-tile">
                                                <div className="ap-stat-value red">{statsVisible ? countedLosses : 0}</div>
                                                <div className="ap-stat-label">Losses</div>
                                            </div>
                                            <div className="ap-stat-tile ap-stat-tile--winrate">
                                                <div className="ap-stat-value">{winRate !== null ? `${winRate}%` : '—'}</div>
                                                <div className="ap-stat-label">Win Rate</div>
                                                {winRate !== null && (
                                                    <div className="ap-winrate-bar-track">
                                                        <div
                                                            className="ap-winrate-bar-fill"
                                                            style={{
                                                                width: statsVisible ? `${winRate}%` : '0%',
                                                                background: winRate >= 60 ? 'var(--green)' : winRate >= 40 ? '#f59e0b' : 'var(--red)'
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ── Favorite teams ── */}
                            {(userInfo.favoriteNFLTeam || userInfo.favoriteNBATeam) && (
                                <div className="ap-card">
                                    <p className="ap-card-title">Favorite Teams</p>
                                    <div className="ap-teams-grid">
                                        {userInfo.favoriteNFLTeam && (
                                            <div className="ap-team-item">
                                                <span className="ap-team-sport">NFL</span>
                                                <span>{userInfo.favoriteNFLTeam}</span>
                                            </div>
                                        )}
                                        {userInfo.favoriteNBATeam && (
                                            <div className="ap-team-item">
                                                <span className="ap-team-sport">NBA</span>
                                                <button className="ap-team-link" onClick={() => handleTeamClick(userInfo.favoriteNBATeam)}>
                                                    {userInfo.favoriteNBATeam}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* ── Statistics ── */}
                            <ProfileStats userId={userInfo.id} />
                        </div>
                    </>
                )}
            </div>

            {/* ── Send Message Modal ── */}
            <Modal show={showMessageModal} onHide={closeMessageModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fa fa-envelope" style={{ marginRight: 8, color: 'var(--flame)' }} />
                        Message {userInfo?.firstName}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {messageSent ? (
                        <p style={{ textAlign: 'center', color: 'var(--green)', fontWeight: 600, margin: '0.5rem 0' }}>
                            <i className="fa fa-check-circle" style={{ marginRight: 6 }} />
                            Message sent!
                        </p>
                    ) : (
                        <>
                            <textarea
                                className="ap-msg-textarea"
                                rows={4}
                                maxLength={500}
                                placeholder={`Send a message to ${userInfo?.firstName}…`}
                                value={messageText}
                                onChange={e => setMessageText(e.target.value)}
                            />
                            <div className="ap-msg-footer">
                                {messageError && <span className="ap-msg-error">{messageError}</span>}
                                <span className="ap-msg-count">{messageText.length}/500</span>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeMessageModal}>Close</Button>
                    {!messageSent && (
                        <Button variant="primary" onClick={handleSendMessage} disabled={messageSending || !messageText.trim()}>
                            {messageSending ? 'Sending…' : 'Send'}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </BaseLayout>
    );
};

export default ProfilePage;
